import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, IconButton, Drawer, List, ListItemButton, ListItemText, Divider, Typography } from '@mui/material';
import { Menu as HamburgerIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import SearchAutocomplete from './SearchAutocomplete';
import { tokens } from '../theme';

// Five primary destinations; everything else lives in the menu drawer and the footer.
const NAV: Array<[string, string]> = [
  ['/states', 'Rents by state'],
  ['/cities', 'Cities'],
  ['/housing-authorities', 'Housing authorities'],
  ['/map', 'Map'],
  ['/reports', 'Reports'],
  ['/pricing', 'Pricing'],
];
const MORE: Array<[string, string]> = [
  ['/expired-listings', 'Expired listings (Pro)'],
  ['/chrome-extension', 'Chrome extension'],
  ['/calculator/cash-flow', 'Cash flow calculator'],
  ['/calculator/purchase-price', 'Purchase price calculator'],
  ['/calculator/break-even', 'Break-even calculator'],
  ['/explorer', 'Investment explorer'],
  ['/glossary', 'Glossary'],
];

// Pages rendered by the backend (not React routes) need a full page load.
const SERVER_PAGES = /^\/housing-authorit/;

const linkSx = {
  color: tokens.ink, textDecoration: 'none', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
  '&:hover': { color: tokens.signal },
} as const;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const go = (path: string) => {
    setOpen(false);
    if (SERVER_PAGES.test(path)) window.location.href = path; else navigate(path);
  };

  return (
    <Box component="header" sx={{ borderBottom: `1px solid ${tokens.line}`, bgcolor: tokens.ground }}>
      <Box sx={{ maxWidth: tokens.container, mx: 'auto', px: { xs: 2, md: 3 }, height: 60, display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
        <IconButton aria-label="Open menu" edge="start" onClick={() => setOpen(true)} sx={{ display: { xs: 'inline-flex', lg: 'none' }, color: tokens.ink }}>
          <HamburgerIcon />
        </IconButton>
        <Box component={RouterLink} to="/" aria-label="fmr8 home"
          sx={{ fontWeight: 800, fontSize: 21, letterSpacing: '-0.02em', color: tokens.ink, textDecoration: 'none', flexShrink: 0 }}>
          fmr<Box component="span" sx={{ color: tokens.signal }}>8</Box>
        </Box>
        <Box sx={{ flex: '0 1 340px', display: { xs: 'none', md: 'block' }, '& .MuiInputBase-root': { height: 36, fontSize: 14 } }}>
          <SearchAutocomplete />
        </Box>
        <Box component="nav" aria-label="Main" sx={{ ml: 'auto', display: { xs: 'none', lg: 'flex' }, gap: 2.75 }}>
          {NAV.map(([path, label]) => SERVER_PAGES.test(path)
            ? <Box key={path} component="a" href={path} sx={linkSx}>{label}</Box>
            : <Box key={path} component={RouterLink} to={path} sx={linkSx}>{label}</Box>)}
        </Box>
        {!loading && (
          <Box component={RouterLink} to={user ? '/account' : '/login'}
            sx={{ ml: { xs: 'auto', lg: 1 }, fontSize: 14, fontWeight: 600, color: tokens.signal, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
            {user ? 'Account' : 'Sign in'}
          </Box>
        )}
      </Box>

      <Drawer anchor="left" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }}>
          <Box sx={{ px: 2, pb: 2 }}><SearchAutocomplete /></Box>
          <List dense>
            {[['/', 'Home'] as [string, string], ...NAV].map(([path, label]) => (
              <ListItemButton key={path} onClick={() => go(path)}><ListItemText primary={label} /></ListItemButton>
            ))}
          </List>
          <Divider />
          <Typography variant="overline" component="p" sx={{ px: 2, pt: 1.5, color: tokens.muted }}>Tools</Typography>
          <List dense>
            {MORE.map(([path, label]) => (
              <ListItemButton key={path} onClick={() => go(path)}><ListItemText primary={label} /></ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;
